import { FiLock as _FiLock } from "@react-icons/all-files/fi/FiLock.js";
import { FiGlobe as _FiGlobe } from "@react-icons/all-files/fi/FiGlobe.js";
import _styled, { default as _styled2 } from "styled-components";
import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { PopoverInteractionKind } from '@blueprintjs/core';
import { Box, Flex, Icon, Link, Popover, Text } from 'core/components';
import { CLOUD_PROVIDERS } from 'app/util/constants';
import $syn from 'app/stores/synthetics/$syn';
import AgentPopoverCard from 'app/views/synthetics/components/AgentPopoverCard';
import { useSynthAgentDrawer } from 'app/views/synthetics/components/agentDrawer/synthAgentDrawerContext';

/** Which left icon(s) to show. Rollout preset uses cloud only. */

/** Minimal shape of the (untyped JS) AgentModel this component reads. */

const ICON_BOX_CSS = {
  '& svg': {
    height: '14px',
    width: 'auto'
  }
};
function CloudOrTypeIcon({
  agent,
  mode
}) {
  if (mode === 'none') {
    return null;
  }
  const provider = agent.get('metadata.cloud_provider');
  const cloudLogo = provider ? CLOUD_PROVIDERS.byId(provider).logo : null;

  // One icon, cloud preferred: a globe for global agents, a padlock for private ones.
  if (mode === 'cloudOrType') {
    const isGlobal = agent.get('agent_type') === 'global';
    const content = cloudLogo ?? /*#__PURE__*/React.createElement(Icon, {
      icon: isGlobal ? _FiGlobe : _FiLock,
      iconSize: 14,
      color: "muted",
      title: isGlobal ? 'Global agent' : 'Private agent'
    });
    return /*#__PURE__*/React.createElement(_StyledFlex, {
      alignItems: "center",
      flex: "0 0 auto",
      mr: "6px",
      overflow: "hidden",
      maxWidth: "22px"
    }, content);
  }
  const wantsCloud = mode === 'cloud' || mode === 'cloudThenType';
  const wantsType = mode === 'type' || mode === 'cloudThenType';
  const TypeIcon = agent.icon;
  const typeEl = wantsType && TypeIcon ? /*#__PURE__*/React.createElement(TypeIcon, {
    width: 14,
    height: 14
  }) : null;
  const cloudEl = wantsCloud && cloudLogo ? cloudLogo : null;
  const content = cloudEl || typeEl;
  if (!content) {
    return null;
  }
  return /*#__PURE__*/React.createElement(_StyledFlex2, {
    alignItems: "center",
    flex: "0 0 auto",
    mr: "6px",
    overflow: "hidden",
    maxWidth: "22px"
  }, content, mode === 'cloudThenType' && cloudEl && typeEl && /*#__PURE__*/React.createElement(Box, {
    ml: "4px"
  }, typeEl));
}

/**
 * Single, configurable agent renderer used across UDE + synthetics surfaces
 * (legend table, agent filter rows/selection, agent links). Rollout preset:
 * cloud icon left (if any) + name + long-form ASN pushed right.
 */
const AgentDisplay = observer(props => {
  const {
    agent: agentProp,
    agentId,
    icon = 'cloud',
    showCloudRegion = false,
    showAsn = false,
    showStatus = false,
    showLocation = false,
    align = 'inline',
    iconPosition = 'left',
    size = 'small',
    compact = false,
    clickable = false,
    linkTo,
    withPopover = false,
    withDetails = false,
    openDetailsOnClick = false
  } = props;
  const drawer = useSynthAgentDrawer();

  // Resolve the agent from the store when only an id is supplied.
  const synAgents = $syn.agents;
  useEffect(() => {
    if (!agentProp && synAgents && !synAgents.hasFetched) {
      synAgents.fetch();
    }
  }, [agentProp, synAgents]);
  let agent = agentProp ?? null;
  if (!agent && agentId != null && synAgents) {
    const result = synAgents.get(agentId);
    agent = (Array.isArray(result) ? result[0] : result) ?? null;
  }
  const small = size === 'small';
  if (!agent) {
    return /*#__PURE__*/React.createElement(Text, {
      small: small,
      muted: true
    }, agentId != null ? `Agent ${agentId}` : 'Unknown agent');
  }
  const asn = showAsn ? agent.get('asn') : undefined;
  const asnText = asn && String(asn) !== '0' ? agent.asFormatted : null;
  const provider = agent.get('metadata.cloud_provider');
  const cloudRegion = showCloudRegion && provider ? agent.cloudRegion : null;
  const location = showLocation ? agent.locationString : null;
  const status = showStatus ? agent.status : null;
  const content = /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center",
    overflow: "hidden",
    width: "100%",
    py: compact ? '1px' : '3px'
  }, iconPosition === 'left' && /*#__PURE__*/React.createElement(CloudOrTypeIcon, {
    agent: agent,
    mode: icon
  }), /*#__PURE__*/React.createElement(Text, {
    small: small,
    ellipsis: true,
    flex: "1 1 auto",
    minWidth: 0
  }, agent.label ?? `Agent ${agent.id ?? ''}`), status?.icon && /*#__PURE__*/React.createElement(Icon, {
    ml: "4px",
    icon: status.icon,
    color: status.color,
    title: status.text,
    iconSize: 12
  }), (cloudRegion || location) && /*#__PURE__*/React.createElement(Text, {
    as: "span",
    ml: "6px",
    muted: true,
    small: true,
    style: {
      whiteSpace: 'nowrap',
      opacity: 0.6
    }
  }, cloudRegion || location), (asnText || iconPosition === 'withAsn') && /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center",
    ml: align === 'spaceBetween' ? 'auto' : '6px',
    pl: align === 'spaceBetween' ? '8px' : 0,
    minWidth: 0
    // Fixed basis when right-aligned so every row's provider info starts at the same x.
    ,
    flex: align === 'spaceBetween' ? '0 0 45%' : '0 1 auto'
  }, iconPosition === 'withAsn' && /*#__PURE__*/React.createElement(CloudOrTypeIcon, {
    agent: agent,
    mode: icon
  }), asnText && /*#__PURE__*/React.createElement(Text, {
    as: "span",
    ellipsis: true,
    muted: true,
    small: true,
    minWidth: 0,
    style: {
      opacity: 0.6
    }
  }, asnText)));
  const opensDetails = openDetailsOnClick && drawer.canOpen && agent.id != null;
  let rendered = content;
  if (opensDetails) {
    const detailsAgentId = agent.id;
    // Stop the click/key from bubbling to an outer row handler (e.g. the tests table row,
    // which would also open the test drawer).
    const openDetails = e => {
      e.stopPropagation();
      drawer.openAgentDrawer(detailsAgentId);
    };
    rendered = /*#__PURE__*/React.createElement("div", {
      onClick: openDetails,
      style: {
        cursor: 'pointer'
      },
      role: "button",
      tabIndex: 0,
      onKeyDown: e => {
        if (e.key === 'Enter' || e.key === ' ') {
          openDetails(e);
        }
      }
    }, content);
  }
  if (withPopover) {
    const canOpenDetails = withDetails && drawer.canOpen && agent.id != null;
    const fullPageHref = withDetails && !drawer.canOpen && agent.id != null ? linkTo ?? `/v4/synthetics/agents/${agent.id}/summary` : undefined;
    return /*#__PURE__*/React.createElement(Popover, {
      interactionKind: PopoverInteractionKind.HOVER,
      hoverOpenDelay: 200,
      minimal: false,
      position: "bottom",
      boundary: "viewport",
      content: /*#__PURE__*/React.createElement(AgentPopoverCard, {
        agent: agent,
        onOpenDetails: canOpenDetails ? () => drawer.openAgentDrawer(agent.id) : undefined,
        fullPageHref: fullPageHref
      })
    }, rendered);
  }
  if (!opensDetails && clickable && agent.id != null) {
    const href = linkTo ?? `/v4/synthetics/agents/${agent.id}/summary`;
    return /*#__PURE__*/React.createElement(Link, {
      to: href,
      style: {
        color: 'inherit',
        textDecoration: 'none',
        display: 'block'
      }
    }, content);
  }
  return rendered;
});
export default AgentDisplay;

/** Cloud icon left (if any) + name + long-form ASN pushed right — the first rollout look. */
export const AGENT_PRESET_COMPACT_ASN = {
  icon: 'cloud',
  showAsn: true,
  align: 'spaceBetween',
  size: 'small',
  compact: true
};

/** Cloud icon left (if any) + name. For rows that pair with a popover, where the ASN is a click away. */
export const AGENT_PRESET_COMPACT = {
  icon: 'cloud',
  size: 'small',
  compact: true
};

/** Name left, cloud icon + long-form ASN as a right-hand column. */
export const AGENT_PRESET_ASN_COLUMN = {
  icon: 'cloud',
  showAsn: true,
  align: 'spaceBetween',
  iconPosition: 'withAsn',
  size: 'small',
  compact: true
};
var _StyledFlex = _styled(Flex)`${ICON_BOX_CSS}`;
var _StyledFlex2 = _styled2(Flex).withConfig({
  displayName: "AgentDisplay___StyledFlex2",
  componentId: "sc-7b3l40-0"
})(["", ""], ICON_BOX_CSS);